/**
 * Email validation regex pattern
 * Matches standard email format: username@domain.extension
 */
const EMAIL_REGEX = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

/**
 * Normalizes an email address by trimming whitespace and converting to lowercase.
 * This ensures case-insensitive email handling throughout the application.
 *
 * @param email - The email string to normalize
 * @returns Normalized email address (trimmed and lowercase), or empty string if input is invalid
 *
 * @example
 * normalizeEmail('  User@Example.COM  ') // returns 'user@example.com'
 * normalizeEmail('USER@EXAMPLE.COM') // returns 'user@example.com'
 * normalizeEmail('') // returns ''
 */
export const normalizeEmail = (email: string): string => {
  if (!email || typeof email !== 'string') {
    return '';
  }
  return email.trim().toLowerCase();
};

/**
 * Validates if a string is a properly formatted email address
 * @param email - The email string to validate
 * @returns true if the email is valid, false otherwise
 */
export const isValidEmail = (email: string): boolean => {
  if (!email || typeof email !== 'string') {
    return false;
  }
  return EMAIL_REGEX.test(email.trim());
};

/**
 * Validates if a password meets minimum requirements
 * @param password - The password string to validate
 * @returns true if the password is valid, false otherwise
 */
export const isValidPassword = (password: string): boolean => {
  if (!password || typeof password !== 'string') {
    return false;
  }
  // Simple validation: password must have at least 1 character
  // Can be extended with more complex rules if needed
  return password.length > 0;
};

/**
 * Validates login form inputs based on whether password is required
 * @param email - The email string to validate
 * @param password - The password string to validate (optional)
 * @param requirePassword - Whether password validation is required
 * @returns true if all required fields are valid, false otherwise
 */
export const isValidLoginInput = (
  email: string,
  password: string = '',
  requirePassword: boolean = false,
): boolean => {
  const emailValid = isValidEmail(email);

  if (!requirePassword) {
    return emailValid;
  }

  return emailValid && isValidPassword(password);
};

/**
 * Get validation error message for email
 * @param email - The email string to validate
 * @returns Error message if invalid, empty string if valid
 */
export const getEmailErrorMessage = (email: string): string => {
  if (!email || (typeof email === 'string' && email.trim() === '')) {
    return 'Please enter your email address';
  }
  if (!isValidEmail(email)) {
    return 'Please enter a valid email address';
  }
  return '';
};

/**
 * Get validation error message for password
 * @param password - The password string to validate
 * @returns Error message if invalid, empty string if valid
 */
export const getPasswordErrorMessage = (password: string): string => {
  if (!password || typeof password !== 'string' || password.length === 0) {
    return 'Please enter your password';
  }
  // Add more validation messages here if needed (e.g., minimum length)
  return '';
};

/**
 * MongoDB ObjectID validation regex pattern
 * Matches exactly 24 hexadecimal characters (case-insensitive)
 */
const OBJECT_ID_REGEX = /^[a-f0-9]{24}$/i;

/**
 * Validates if a string is a valid MongoDB ObjectID format
 * @param id - The string to validate
 * @returns true if the string is a valid ObjectID format, false otherwise
 */
export const isValidObjectId = (id: string): boolean => {
  if (!id || typeof id !== 'string') {
    return false;
  }
  return OBJECT_ID_REGEX.test(id);
};
